import { supabase } from './supabase'

/**
 * Read a whole table, rather than the first thousand rows of it.
 *
 * THE FAILURE THIS EXISTS TO PREVENT
 *
 * PostgREST caps every response at `db.max_rows`, which Supabase sets to 1000
 * by default. Past that it returns the first thousand rows **and no error**.
 * There is nothing to catch, nothing to log, and nothing on screen to see.
 *
 * That is survivable for the roster. It is not survivable for `agent_tags`,
 * because `no-outreach` and `no-bulk-email` live there: past a thousand tag
 * rows, some agent's "do not email me" simply stops existing, every guard in
 * `campaigns.ts` reports all-clear, and the first symptom is that agent getting
 * an email.
 *
 * THREE THINGS HERE ARE NOT OPTIONAL, AND EACH ONE IS A BUG THIS FILE ALREADY
 * HAD
 *
 * 1. **A total order.** `.range()` is `LIMIT`/`OFFSET`, and offset paging is
 *    only sound when the ordering has no ties. `order by last_name` over 785
 *    agents has plenty, and two pages are two separate queries: a name
 *    straddling the boundary can come back on both pages, or on neither. Every
 *    caller therefore passes a column list ending in something unique.
 *
 * 2. **Stepping by what actually came back, not by `PAGE`.** "A short page
 *    means the end of the data" is precisely what a server-side cap looks like.
 *    If `db.max_rows` were ever set below `PAGE`, asking for 500 and getting
 *    200 would have been read as "that is all of them" — the original bug,
 *    wearing a paging loop as a disguise.
 *
 * 3. **Checking the total.** PostgREST will say how many rows there really are.
 *    Asking costs one count per page and turns "I think I have all of them"
 *    into something verified. Nothing that a send depends on should be read on
 *    a guess.
 */

/** Comfortably under any sane `max_rows` — but never assumed to be honoured. */
export const PAGE = 500

/** A hard stop, so a bug here cannot become an infinite loop against the API. */
const MAX_PAGES = 500

type Paged = {
  eq: (column: string, value: string) => Paged
  order: (column: string) => Paged
  range: (
    from: number,
    to: number,
  ) => PromiseLike<{
    data: unknown[] | null
    error: { message: string } | null
    count: number | null
  }>
}

export type FetchAllResult<T> = {
  rows: T[]
  error: string | null
  /** True when the answer is known to be incomplete. Never guessed. */
  truncated: boolean
}

export async function fetchAll<T>(
  table: string,
  columns = '*',
  /**
   * The sort, most significant first, and the last one MUST be unique within
   * the table — a primary key or part of one. Without it the paging is unsound.
   */
  order: readonly string[] = [],
  /** An optional `column = value` filter, for tables read in part. */
  where?: readonly [string, string],
): Promise<FetchAllResult<T>> {
  const rows: T[] = []
  let expected: number | null = null
  let from = 0

  for (let page = 0; page < MAX_PAGES; page++) {
    /**
     * Built through a structural type rather than the library's own.
     *
     * `.select()`, `.eq()` and `.order()` each return a *different* builder
     * type, so a loop that adds an unknown number of sort columns cannot be
     * written against them without a cast somewhere. This says exactly which
     * three methods are used and what comes back, which is the narrowest
     * honest description of what this function needs.
     */
    let q = supabase.from(table).select(columns, { count: 'exact' }) as unknown as Paged
    if (where) q = q.eq(where[0], where[1])
    for (const col of order) q = q.order(col)
    const { data, error, count } = await q.range(from, from + PAGE - 1)

    if (error) {
      /**
       * Partial results are not returned. A caller handed 800 of 1400 tag rows
       * with an error beside them will use the 800 — which is this whole
       * problem restated one level up.
       */
      return { rows: [], error: error.message, truncated: false }
    }

    if (expected === null && typeof count === 'number') expected = count

    const batch = (data ?? []) as unknown as T[]
    rows.push(...batch)

    // Nothing came back: there is no more, whatever the count said.
    if (batch.length === 0) break
    // Advance by what the server actually gave, not by what was asked for.
    from += batch.length
    if (expected !== null && rows.length >= expected) break
  }

  /**
   * The only honest answer to "did I get all of it". If the database said 1400
   * and 1200 arrived, say so — an incomplete tag list must never look whole.
   */
  const truncated = expected !== null ? rows.length < expected : false
  return { rows, error: null, truncated }
}
